import { useCallback, useEffect, useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import Header from '../../../../../shared/settings/SettingsPageHeader';
import Tabs from '../../../../../generic/Tabs';
import { useParams, useQuery } from '../../../../../../contexts/RouterContext';
import { useTeamGroup } from '../../../../../../hooks/api/teams/teamGroups';
import Loader from '../../../../../generic/Loader';
import { useRoles } from '../../../../../../hooks/api';
import TeamGroupMembers from './members';
import EditTeamGroupModal from './EditTeamGroupModal';
import TeamGroupTeamsTable from './teams/TeamGroupTeamsTable';
import useTeamPermissions from '../../hooks/useTeamPermissions';
import type { FullTeamGroupResponse } from '../../../../../../../shared/types/routes/teamGroups';
import styles from './TeamGroupManagement.module.less';

type Props = {
  isLoading: boolean;
  activeTab?: 'teams' | 'members';
  setActiveTab: (tab: 'teams' | 'members') => void;
  teamGroup?: FullTeamGroupResponse;
  defaultRoleName: string;
};

/**
 * TeamGroupManagement is the page for managing a team group.
 * It displays the assigned teams, as well as the members of the team group.
 * It allows for editing the team group name, as well as adding and removing teams from the group.
 */
const TeamGroupManagementComponent: VoidFunctionComponent<Props> = ({
  activeTab,
  setActiveTab,
  isLoading,
  teamGroup,
  defaultRoleName,
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { canEditAnyTeam, canEditThisTeam } = useTeamPermissions(teamGroup?.id ?? 0);
  const hasEditPermission = canEditAnyTeam || canEditThisTeam;
  return isLoading || !teamGroup ? (
    <Loader />
  ) : (
    <>
      <Header heading={teamGroup.name}>
        <Header.BackLink to="/account/settings/organization/teams?tab=teamGroups" text="Back to Team Groups" />
        <Header.Paragraph>
          <div className={styles.defaultRoleText}>
            <span className={styles.defaultRoleTextLabel}>
              Default Role
            </span>
            {defaultRoleName}
          </div>
        </Header.Paragraph>
        <Header.TooltipButton
          id="edit-team-group"
          isDisabled={!hasEditPermission}
          onClick={() => setIsEditing(true)}
          showTooltip={!hasEditPermission}
          tooltip="You do not have permission to edit this team group."
        >
          Edit Team Group
        </Header.TooltipButton>
      </Header>
      <Tabs>
        <Tabs.Tab
          label="Assigned Teams"
          isActive={!activeTab || activeTab === 'teams'}
          onSelect={() => setActiveTab('teams')}
        />
        <Tabs.Tab
          label="Members"
          isActive={activeTab === 'members'}
          onSelect={() => setActiveTab('members')}
        />
      </Tabs>
      {activeTab === 'teams' ? (
        <TeamGroupTeamsTable
          teamGroupId={teamGroup.id}
          teamGroupName={teamGroup.name}
          teamGroupTeams={teamGroup.teams}
          isLoading={isLoading}
        />
      ) : (
        <TeamGroupMembers
          teamGroupId={teamGroup.id}
          teamGroupName={teamGroup.name}
          members={teamGroup.members}
          isLoading={isLoading}
        />
      )}
      <EditTeamGroupModal
        isOpen={isEditing}
        onHide={() => setIsEditing(false)}
        teamGroupId={teamGroup.id}
        teamGroupName={teamGroup.name}
        selectedRoleId={teamGroup.defaultRoleId}
      />
    </>
  );
};

/**
 * Stateful container for the TeamGroupManagementComponent.
 * This manages the state of the tabs and the team group.
 */
const TeamGroupManagement = () => {
  const { id } = useParams<{ id: number }>();
  const { data: teamGroup, isLoading: isLoadingTeam } = useTeamGroup(id);
  const { data: roles, isLoading: isLoadingRoles } = useRoles();
  const defaultRoleName = roles?.find((role) => role.id === teamGroup?.defaultRoleId)?.name ?? '...';
  const [query, setQuery] = useQuery<{ tab?: 'teams' | 'members' }>();

  const setActiveTab = useCallback((tab: 'teams' | 'members') => {
    if (!tab || (tab !== 'teams' && tab !== 'members')) {
      setQuery({ tab: 'teams' });
    } else {
      setQuery({ tab });
    }
  }, [setQuery]);

  //  Ensure that there is always a tab set.
  useEffect(() => {
    if (!query?.tab) {
      setActiveTab('teams');
    }
  }, [query?.tab, setActiveTab]);

  return (
    <TeamGroupManagementComponent
      activeTab={query.tab}
      setActiveTab={setActiveTab}
      isLoading={isLoadingTeam || isLoadingRoles}
      teamGroup={teamGroup}
      defaultRoleName={defaultRoleName}
    />
  );
};

export default TeamGroupManagement;
